import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Tag,
  Modal,
  Drawer,
  Button,
  Spin,
  Avatar,
  notification,
  Tooltip,
  Input,
  InputNumber,
  Switch,
  Popconfirm
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CheckCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { businessAtom, reloadLayawayBinsAtom, selectedLayawayAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';

function LayawayBins() {
  const [width, setWidth] = useState(window.innerWidth);
  const defualtBin = {
    tag: ''
  };
  const business = useRecoilValue(businessAtom);
  const { t } = useTranslation();
  const [reloadLayawayBins, setReloadLayawayBins] = useRecoilState(reloadLayawayBinsAtom);
  const setSelectedLayaway = useSetRecoilState(selectedLayawayAtom);
  const [filterString, setFilterString] = useState('');
  const [filteredBins, setFilteredBins] = useState([]);
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLayaway, setLoadingLayaway] = useState(false);
  const [editBinData, setEditBinData] = useState({
    show: false,
    bin: null
  });

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getBins() {
    if (business && !loading) {
      setLoading(true);
      api.get(`layaway/business/bins/${business.id}`)
        .then((response) => {
          if (response.data.success) {
            setBins(response.data.data.results);
          } else {
            showMessage(response.data.error);
          }
        })
        .catch((error) => {
          showMessage(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function updateBin() {
    if (business && !loading) {
      setLoading(true);
      api.put('layaway/business/bins', {
        ...editBinData?.bin,
        LayawayBinId: editBinData?.bin?.id
      })
        .then((response) => {
          if (response.data.success) {
            setBins(response.data.data.results);
            if (response.data.message) {
              showMessage(response.data.message);
            }
          } else {
            showMessage(response.data.error);
          }
        })
        .catch((error) => {
          showMessage(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function createBin() {
    if (business && !loading) {
      setLoading(true);
      api.post('layaway/business/bins', {
        ...editBinData?.bin,
        BusinessId: business?.id
      })
        .then((response) => {
          if (response.data.success) {
            setBins(response.data.data.results);
            setEditBinData({
              show: false,
              bin: null
            });
            if (response.data.message) {
              showMessage(response.data.message);
            }
          } else {
            showMessage(response.data.error);
          }
        })
        .catch((error) => {
          showMessage(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function freeupBin() {
    setLoading(true);
    api.post(`layaway/bins/freeup/${editBinData?.bin?.id}`, null)
      .then((response) => {
        if (response.data.success) {
          setBins(response.data.data.results);
          setEditBinData({
            show: false,
            bin: null
          });
          if (response.data.message) {
            showMessage(response.data.message);
          }
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function getLayaway(layawayId) {
    setLoadingLayaway(true);
    api.get(`layaway/${layawayId}`)
      .then((response) => {
        if (response.data.success) {
          setSelectedLayaway(response.data.data);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        showMessage(error.message);
      })
      .finally(() => {
        setLoadingLayaway(false);
      });
  }

  function getBinClassnames(bin) {
    let bgColor = 'bg-white';
    let borderColor = 'border-black';
    if (bin.blocked) {
      bgColor = 'bg-red-500';
      borderColor = 'border-red-500';
    }
    if (bin.layawayId) {
      bgColor = 'bg-yellow-600';
      borderColor = 'border-yellow-600';
    }
    return `col-span-1 border-2 p-2 ${bgColor} ${borderColor} bg-opacity-25 rounded-md`;
  }

  function filterBins() {
    if (filterString?.length > 0) {
      const tempBins = _.filter(bins, (bin) => bin.tag.toLowerCase().includes(filterString.toLocaleLowerCase()) ||
                                               bin.layaway?.code.toLowerCase().includes(filterString.toLocaleLowerCase()));
      setFilteredBins(tempBins);
    } else {
      setFilteredBins(bins);
    }
  }

  useEffect(() => {
    getBins();
  }, [business]);

  useEffect(() => {
    filterBins();
  }, [bins, filterString]);

  useEffect(() => {
    if (reloadLayawayBins) {
      setReloadLayawayBins(false);
      getBins();
    }
  }, [reloadLayawayBins]);

  useEffect(() => {
    setWidth(window.innerWidth);
  }, [window.innerWidth]);

  return (
    <div>
      <PageHeader
        title={t('bins')}
        tags={
          <Tag
            color="#2db7f5"
            icon={business ? <CheckCircleOutlined /> : <SyncOutlined spin />}
          >
            {business ? business.name : `${t('loading')}...`}
          </Tag>
        }
        extra={[
          <Button
            type="primary"
            onClick={() => {
              setEditBinData({
                show: true,
                bin: defualtBin
              });
            }}
          >
            {t('create')}
          </Button>,
          <Button
            type="primary"
            onClick={() => {
              getBins();
            }}
          >
            {t('reload')}
          </Button>
        ]}
      />
      <Spin spinning={loading}>
        <Input
          addonBefore={t('search')}
          className="mb-2"
          value={filterString}
          onChange={(obj) => setFilterString(obj.target.value)}
        />
        <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-4">
          {_.map(filteredBins, (bin) => (
            <button
              className={getBinClassnames(bin)}
              type="button"
              onClick={() => {
                setEditBinData({
                  show: true,
                  bin
                });
              }}
            >
              <p className="duration-200 text-center text-lg hover:font-bold hover:text-xl">{bin.tag}</p>
              {bin.layaway && (
                <Tag className="text-center">{bin.layaway?.code}</Tag>
              )}
            </button>
          ))}
        </div>
      </Spin>
      <Drawer
        placement="right"
        closable={!loading}
        width={width < 350 ? width : 350}
        title={editBinData?.bin?.id ? t('edit_bin') : t('create_bin')}
        open={editBinData.show}
        onClose={() => setEditBinData({
          show: false,
          bin: null
        })}
        footer={[
          <div className="flex space-x-2">
            <Button
              className="ml-auto"
              key="ok"
              type="primary"
              loading={loading}
              onClick={() => (editBinData?.bin?.id ? updateBin() : createBin())}
            >
              {editBinData?.bin?.id ? t('update') : t('create')}
            </Button>
          </div>
        ]}
      >
        <div className="space-y-2">
          <div>
            <strong>{t('tag')}</strong>
            <Input
              disabled={loading}
              onChange={(obj) => setEditBinData({
                ...editBinData,
                bin: {
                  ...editBinData.bin,
                  tag: obj.target.value
                }
              })}
              value={editBinData?.bin?.tag}
            />
          </div>
          {editBinData?.bin?.id && (
            <>
              <div>
                <strong>{t('blocked')}</strong>
                <Switch
                  className="ml-2"
                  disabled={loading}
                  checked={editBinData?.bin?.blocked}
                  onChange={(obj) => setEditBinData({
                    ...editBinData,
                    bin: {
                      ...editBinData?.bin,
                      blocked: obj
                    }
                  })}
                />
              </div>
              <div>
                <p className="font-bold">{t('ordering')}</p>
                <InputNumber
                  className="w-full"
                  disabled={loading}
                  onChange={(obj) => setEditBinData({
                    ...editBinData,
                    bin: {
                      ...editBinData.bin,
                      ordering: obj
                    }
                  })}
                  value={editBinData?.bin?.ordering}
                />
              </div>
            </>
          )}
          {editBinData?.bin?.layaway && (
            <>
              <Popconfirm
                title={t('are_you_sure_freeup_bin_prompt')}
                okText={t('yes')}
                cancelText={t('no')}
                onConfirm={() => freeupBin()}
              >
                <Button
                  loading={loading}
                  type="primary"
                  className="w-full"
                >
                  {t('freeup_bin')}
                </Button>
              </Popconfirm>
              <Button
                type="primary"
                className="w-full space-x-2"
                loading={loadingLayaway}
                onClick={() => getLayaway(editBinData?.bin?.layawayId)}
              >
                <span>{t('layaway')}</span>
                <Tag className="text-center">{editBinData?.bin?.layaway?.code}</Tag>
              </Button>
            </>
          )}
        </div>
      </Drawer>
    </div>
  );
}

export default LayawayBins;
